import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProductTypeENUM, ProductWithAttributeAndValuePair } from 'src/app/models/enums/ProductEnums';
import { Attribute, NumericChange } from 'src/app/models/product-models/views/Attribute';
import { AttributeValue, ModelValue } from 'src/app/models/product-models/views/AttributeValue';
import {
  AttributeMap,
  AttributeOptionalValue,
  AttributePair,
  Product,
  ProductEventsENUM,
} from 'src/app/models/product-models/views/Product';
import { ProductBlueprint } from 'src/app/models/product-models/views/ProductBlueprint';
import { Object3D } from 'three';
import { AttributeEvent, ChildAddedEvent } from '../../interfaces/ProductEvents';
import { EventBusService } from './event-bus.service';
import { HIDDEN_WITHOUT_REFERENCES } from '../attribute/attribute.component';

@Injectable()
export class ReferenceControllerService {
  private _subs: Subscription[] = [];

  private _referenceMap: Map<AttributeOptionalValue, ProductWithAttributeAndValuePair[]> = new Map<
    AttributeOptionalValue,
    ProductWithAttributeAndValuePair[]
  >();

  private _keyMap: Map<AttributeValue | Attribute, AttributeOptionalValue> = new Map<
    AttributeValue,
    AttributeOptionalValue
  >();

  constructor(private eventBusService: EventBusService) {
    this._subscribeToSeriesChanged();
  }

  private _subscribeToSeriesChanged() {
    this._subs.push(
      this.eventBusService.seriesChanged$.subscribe((series) => {
        const allProductBlueprints = series.flattenedProductBlueprints;

        for (const productBlueprint of allProductBlueprints) {
          for (const attribute of productBlueprint.attributes) {
            this._recursivelyCheckReferenceAttributes(attribute);
          }
        }
        this._subscribeToConfigurationAdded();
        this._subscribeToProductAdded();
        this._subscribeToProductRemoved();
      }),
    );
  }

  /* public isReferenceAttributeValueAvailable(attributeValue: AttributeValue) {
    if (attributeValue.attributeValueIds.length > 0) {
      if (!this._referencedAttributes.has(attributeValue)) {
        return false;
      }
      // eslint-disable-next-line
      if (this._referencedAttributes.get(attributeValue)!.length === 0) {
        return false;
      }
    }
    if (attributeValue instanceof ModelValue) {
      let found = false;
      for (const productRelationship of attributeValue.productRelationships) {
        if (!(productRelationship.childProduct instanceof ProductBlueprint)) {
          continue;
        }

        if (this._referenceAttributesCanBeApplied(this._attributeSettings, productRelationship.childProduct)) {
          found = true;
        }
      }
      return found;
    }

    return true;
  }

    private _referenceAttributesCanBeApplied(map: AttributeMap, productBlueprint: ProductBlueprint): boolean {
    for (const attributeSetting of map.values()) {
      if (attributeSetting && attributeSetting.atributeValue instanceof AttributeValue) {
        let found = false;
        if (productBlueprint.containsAttributeValue(attributeSetting.atributeValue)) {
          found = true;
        }

        if (!found) {
          return found;
        }

        if (attributeSetting.map) {
          return this._referenceAttributesCanBeApplied(attributeSetting.map, productBlueprint);
        }
      }
    }

    return true;
  }

  public getProductsReferencedAttributes(product: Product) {
    const attributeAndValuePairs: AttributePair[] = [];

    for (const [attribute, attributeSetting] of this._attributeSettings) {
      if (attributeSetting && attributeSetting.atributeValue instanceof AttributeValue) {
        const productsWithAttributeAndValuePair = this._referencedAttributes.get(attributeSetting.atributeValue);
        if (productsWithAttributeAndValuePair) {
          for (const productWithAttributeAndValuePair of productsWithAttributeAndValuePair) {
            if (productWithAttributeAndValuePair.product === product && this._attributeSettings.get(attribute)) {
              attributeAndValuePairs.push({
                attribute: productWithAttributeAndValuePair.attribute,
                attributeValue: productWithAttributeAndValuePair.attributeValue!,
              });
            }
          }
        }
      }
    }

    return attributeAndValuePairs;
  }

  */
  private recursivelyFillProductReferencedAttributes(childProduct: Product, parentProduct: Product) {
    const attributeAndValuePairs: AttributePair[] = [];
    const parentsAttributePairs = parentProduct.attributeSettings.generateAttributePairs();
    for (const parentsAttributePair of parentsAttributePairs) {
      const key = this._getKey(
        parentsAttributePair.attribute,
        typeof parentsAttributePair.attributeValue === 'number' ? undefined : parentsAttributePair.attributeValue,
      );

      if (key) {
        const productsWithAttributeAndValuePair = this._referenceMap.get(key);
        if (productsWithAttributeAndValuePair) {
          for (const productWithAttributeAndValuePair of productsWithAttributeAndValuePair) {
            if (productWithAttributeAndValuePair.product === childProduct) {
              if (
                parentsAttributePair.attribute instanceof NumericChange &&
                parentsAttributePair.attribute.defaultValue !== undefined &&
                parentsAttributePair.attribute.defaultValue !== null
              ) {
                attributeAndValuePairs.push({
                  attribute: productWithAttributeAndValuePair.attribute,
                  attributeValue: parentsAttributePair.attribute.defaultValue,
                });
                continue;
              }
              if (!productWithAttributeAndValuePair.attributeValue) continue;

              attributeAndValuePairs.push({
                attribute: productWithAttributeAndValuePair.attribute,
                attributeValue: productWithAttributeAndValuePair.attributeValue,
              });
            }
          }
        }
      }
    }
    return attributeAndValuePairs;
  }

  private _getProductsReferencedAttributes(parentProduct: Product, childProduct: Product) {
    let attributeAndValuePairs: AttributePair[] = [];
    attributeAndValuePairs = this.recursivelyFillProductReferencedAttributes(childProduct, parentProduct);

    return attributeAndValuePairs;
  }

  private _checkIfProductAttributesNeedToBeResetDueToRequiringReference(product: Product) {
    for (const attribute of product.productBlueprint.attributes) {
      this._recursivelyCheckIfProductAttributesNeedToBeResetDueToRequiringReference(product, attribute);
    }
  }

  private _recursivelyCheckIfProductAttributesNeedToBeResetDueToRequiringReference(
    product: Product,
    attribute: Attribute,
  ) {
    let alreadySet = false;
    for (const attributeValue of attribute.attributeValues) {
      if (
        attribute.displayParameters &&
        attribute.displayParameters[HIDDEN_WITHOUT_REFERENCES] === true &&
        !(attribute instanceof NumericChange)
      ) {
        if (
          this.checkIfAttributeAndValueReferenceAnything(attribute, attributeValue) === false &&
          product.attributeSettings.getAttributeValueFromAttribute(attribute) === attributeValue
        ) {
          if (
            attribute.defaultValue instanceof AttributeValue &&
            this.checkIfAttributeAndValueReferenceAnything(attribute, attribute.defaultValue)
          ) {
            product.setAttribute(attribute, attribute.defaultValue);
            alreadySet = true;
          } else {
            for (const attributeOfAttributeValue of attribute.attributeValues) {
              if (this.checkIfAttributeAndValueReferenceAnything(attribute, attributeOfAttributeValue)) {
                product.setAttribute(attribute, attributeOfAttributeValue);
                alreadySet = true;
                break;
              }
            }
          }
        }
        if (alreadySet) break;
      }
      for (const attributeOfAttributeValue of attributeValue.attributes)
        this._recursivelyCheckIfProductAttributesNeedToBeResetDueToRequiringReference(
          product,
          attributeOfAttributeValue,
        );
    }
  }

  /*  private _applyReferencesToProduct(product: Product) {
    for (const [, productsWithAttributeAndValuePair] of this._referenceMap) {
      for (const productWithAttributeAndValuePair of productsWithAttributeAndValuePair) {
        if (product === productWithAttributeAndValuePair.product) {
          product.setAttribute(
            productWithAttributeAndValuePair.attribute,
            productWithAttributeAndValuePair.attributeValue,
          );
        }
      }
    }
  }
 */

  private _getReferenceAttributePairs(product: Product, productsToApplyFrom: Product[]) {
    const referencedAttributes: AttributePair[] = [];
    let parent: Object3D | null = product;
    while (parent !== null) {
      if (parent instanceof Product && (productsToApplyFrom.includes(parent) || productsToApplyFrom.length === 0)) {
        const referencedAttributesOfParent = this._getProductsReferencedAttributes(parent, product);

        for (const attributePairOfParent of referencedAttributesOfParent) {
          let found = false;
          for (const attributePair of referencedAttributes) {
            if (attributePair.attribute === attributePairOfParent.attribute) {
              attributePair.attributeValue = attributePairOfParent.attributeValue;
              found = true;
            }
          }
          if (!found) {
            referencedAttributes.push(attributePairOfParent);
          }
        }
      }

      parent = parent.parent;
    }
    return referencedAttributes;
  }

  private _applyInitialReferencesToProduct(product: Product, productsToApplyFrom: Product[] = []) {
    if (
      product.productType === ProductTypeENUM.PositionalGhost ||
      product.productType === ProductTypeENUM.Preview ||
      product.productType === ProductTypeENUM.LinkedPreview
    ) {
      //product.setDefaultAttributes();
      return;
    }

    const referencedAttributes = this._getReferenceAttributePairs(product, productsToApplyFrom);

    product.setDefaultAttributes(referencedAttributes);

    this.eventBusService.publishProductDefaultSettingsSet(product);
  }

  //Initialy designed to get applied when a product is added to islandbuilder, where we need to apply island builders references to product
  //so we add island to productsToApplyFrom
  private _applyReferencesToProduct(product: Product, productsToApplyFrom: Product[] = []) {
    if (
      product.productType === ProductTypeENUM.PositionalGhost ||
      product.productType === ProductTypeENUM.Preview ||
      product.productType === ProductTypeENUM.LinkedPreview
    ) {
      //product.setDefaultAttributes();
      return;
    }

    const referencedAttributes = this._getReferenceAttributePairs(product, productsToApplyFrom);

    for (const attributePair of referencedAttributes) {
      product.setAttribute(attributePair.attribute, attributePair.attributeValue);
    }
  }

  public isReferenceAttributeValueAvailable(product: Product, attribute: Attribute, attributeValue: AttributeValue) {
    if (attributeValue.attributeValueIds.length > 0) {
      const key = this._getKey(attribute, attributeValue);
      if (!key) {
        return false;
      }
      if (!this._referenceMap.has(key)) {
        return false;
      }
      // eslint-disable-next-line
      if (this._referenceMap.get(key)!.length === 0) {
        return false;
      }
    }
    if (attributeValue instanceof ModelValue) {
      let found = false;
      for (const productRelationship of attributeValue.productRelationships) {
        if (!(productRelationship.childProduct instanceof ProductBlueprint)) {
          continue;
        }

        if (
          product.attributeSettings.referenceAttributesCanBeAppliedToProductBlueprint(productRelationship.childProduct)
        ) {
          found = true;
        }
      }
      return found;
    }

    return true;
  }

  private _referenceAttributesCanBeApplied(map: AttributeMap, productBlueprint: ProductBlueprint): boolean {
    for (const attributeSetting of map.values()) {
      if (
        attributeSetting &&
        attributeSetting.attributeValue instanceof AttributeValue &&
        attributeSetting.attributeValue.attributeValues.length > 0
      ) {
        let found = false;
        if (productBlueprint.containsAttributeValue(attributeSetting.attributeValue)) {
          found = true;
        }

        if (!found) {
          return found;
        }

        if (attributeSetting.map) {
          return this._referenceAttributesCanBeApplied(attributeSetting.map, productBlueprint);
        }
      }
    }

    return true;
  }

  private _recursivelyCheckReferenceAttributes(attribute: Attribute) {
    if (attribute instanceof NumericChange) {
      const key = this._getKey(attribute, undefined);
      if (attribute.attributes.length > 0 && !key) {
        const attributeOptionalValue = { attribute: attribute, attributeValue: undefined };
        this._referenceMap.set(attributeOptionalValue, []);
        this._keyMap.set(attribute, attributeOptionalValue);
      }
      return;
    }
    for (const attributeValue of attribute.attributeValues) {
      const key = this._getKey(attribute, attributeValue);
      if (attribute.attributes.length > 0 && attributeValue.attributeValues.length > 0 && !key) {
        const attributeOptionalValue = { attribute: attribute, attributeValue: attributeValue };
        this._referenceMap.set(attributeOptionalValue, []);
        this._keyMap.set(attributeValue, attributeOptionalValue);
      }
      for (const attributeOfAttributeValue of attributeValue.attributes) {
        this._recursivelyCheckReferenceAttributes(attributeOfAttributeValue);
      }
    }
  }

  public checkIfAttributeAndValueReferenceAnything(attribute: Attribute, attributeValue: AttributeValue) {
    const key = this._getKey(attribute, attributeValue instanceof AttributeValue ? attributeValue : undefined);
    if (!key) {
      return false;
    }
    const productWithAttributeAndValuePairs = this._referenceMap.get(key);
    if (!productWithAttributeAndValuePairs) {
      return false;
    }
    return productWithAttributeAndValuePairs.length > 0 ? true : false;
  }

  private _handleAttributeChange = (
    attributeReference: Attribute,
    attributeValueReference: AttributeValue | number,
    parent: Product,
  ) => {
    //Produkt mora tudi chekirat, kateri referencirajo njega in si ustrezno nastavit
    const referencedAttributePairs = this._getReferenceAttributePairs(parent, []);
    for (const referencedAttributePair of referencedAttributePairs) {
      parent.setAttribute(referencedAttributePair.attribute, referencedAttributePair.attributeValue);
    }
    if (attributeReference.attributes.length === 0) return;
    if (attributeValueReference instanceof AttributeValue) {
      if (attributeValueReference.attributeValues.length === 0) {
        return;
      }
    }

    const key = this._getKey(
      attributeReference,
      attributeValueReference instanceof AttributeValue ? attributeValueReference : undefined,
    );

    if (!key) {
      return;
    }
    const productWithAttributeAndValuePairs = this._referenceMap.get(key);

    if (!productWithAttributeAndValuePairs) {
      return;
    }

    for (const { product, attribute, attributeValue } of productWithAttributeAndValuePairs) {
      if (
        product.isAncestor(parent) ||
        (attributeReference.builderData && attributeReference.builderData.sameProductReference)
      ) {
        product.setAttribute(attribute, attributeValue ? attributeValue : attributeValueReference);
      }
    }
  };

  private _addProductToReferenceMap(product: Product) {
    for (const attribute of product.productBlueprint.attributes) {
      this._recursivelyAddProductToReferenceMap(product, attribute);
    }
  }

  private _removeProductFromReferenceMap(product: Product) {
    for (const attribute of product.productBlueprint.attributes) {
      this._recursivelyRemoveProductFromReferenceMap(product, attribute);
    }
  }

  private _recursivelyRemoveProductFromReferenceMap(product: Product, attribute: Attribute) {
    for (const referenceAttributeProductBlueprintPair of attribute.referenceAttributes) {
      const referenceAttribute = referenceAttributeProductBlueprintPair.attribute;

      if (attribute instanceof NumericChange) {
        const key = this._getKey(referenceAttribute, undefined);
        if (!key) {
          continue;
        }
        const referenceMapArray = this._referenceMap.get(key);
        if (referenceMapArray) {
          // Find and remove the specific entry for this product and attribute
          const index = referenceMapArray.findIndex(
            (entry) => entry.product === product && entry.attribute === attribute && entry.attributeValue === undefined,
          );
          if (index !== -1) referenceMapArray.splice(index, 1);
        }
      }

      for (const attributeValue of attribute.attributeValues) {
        for (const referenceAttributeValue of attributeValue.referenceValues) {
          const key = this._getKey(referenceAttribute, referenceAttributeValue);
          if (!key) {
            continue;
          }
          const referenceMapArray = this._referenceMap.get(key);
          if (referenceMapArray) {
            // Find and remove the specific entry for this product, attribute, and attributeValue
            const index = referenceMapArray.findIndex(
              (entry) =>
                entry.product === product && entry.attribute === attribute && entry.attributeValue === attributeValue,
            );
            if (index !== -1) referenceMapArray.splice(index, 1);
          }
        }
      }
    }
    // Recurse on nested attributes of the attributeValues
    for (const attributeValue of attribute.attributeValues) {
      for (const attributeOfAttributeValue of attributeValue.attributes) {
        this._recursivelyRemoveProductFromReferenceMap(product, attributeOfAttributeValue);
      }
    }
  }

  private _recursivelyAddProductToReferenceMap(product: Product, attribute: Attribute) {
    for (const referenceAttributeProductBlueprintPair of attribute.referenceAttributes) {
      if (!product.checkIfProductAncestorHasProductBlueprint(referenceAttributeProductBlueprintPair.productBlueprint))
        continue;
      const referenceAttribute = referenceAttributeProductBlueprintPair.attribute;

      if (attribute instanceof NumericChange) {
        const key = this._getKey(referenceAttribute, undefined);
        if (!key) {
          continue;
        }
        const referenceMapArray = this._referenceMap.get(key);
        if (referenceMapArray) {
          referenceMapArray.push({ product: product, attribute: attribute, attributeValue: undefined });
        }
      }

      for (const attributeValue of attribute.attributeValues) {
        for (const referenceAttributeValue of attributeValue.referenceValues) {
          const key = this._getKey(referenceAttribute, referenceAttributeValue);
          if (!key) {
            continue;
          }
          const referenceMapArray = this._referenceMap.get(key);

          if (referenceMapArray) {
            referenceMapArray.push({ product: product, attribute: attribute, attributeValue: attributeValue });
          }
        }
      }
    }
    for (const attributeValue of attribute.attributeValues) {
      for (const attributeOfAttributeValue of attributeValue.attributes) {
        this._recursivelyAddProductToReferenceMap(product, attributeOfAttributeValue);
      }
    }
  }

  private _subscribeToProductAdded() {
    this._subs.push(
      this.eventBusService.productAdded$.subscribe((productAddedEvent) => {
        const product = productAddedEvent.product;
        this.handleProductAdded(product);
      }),
    );
  }

  private handleProductAdded(product: Product) {
    product.addEventListener(ProductEventsENUM.AttributeChangeEvent, (event: AttributeEvent) => {
      if (event.data) {
        this._handleAttributeChange(event.data.attribute, event.data.attributeValue, product);
        this._checkIfProductAttributesNeedToBeResetDueToRequiringReference(product);
      }
    });
    product.addEventListener(ProductEventsENUM.ChildAdded, (event: ChildAddedEvent) => {
      this._addProductToReferenceMap(event.data);
      event.data.traverse((child) => {
        if (child instanceof Product) {
          this._addProductToReferenceMap(child);
        }
      });
      this._applyReferencesToProduct(event.data, [product]);
    });
    this._addProductToReferenceMap(product);

    this._applyInitialReferencesToProduct(product);
  }

  private _subscribeToProductRemoved() {
    this._subs.push(
      this.eventBusService.productRemoved$.subscribe((product) => {
        this._removeProductFromReferenceMap(product);
      }),
    );
  }

  private _subscribeToConfigurationAdded() {
    this._subs.push(
      this.eventBusService.configurationAdded$.subscribe((configuration) => {
        this.handleProductAdded(configuration);
      }),
    );
  }

  private _getKey(
    attribute: Attribute,
    attributeValue: AttributeValue | undefined,
  ): AttributeOptionalValue | undefined {
    if (attributeValue) {
      return this._keyMap.get(attributeValue);
    } else {
      return this._keyMap.get(attribute);
    }

    /*     for (const key of this._referenceMap.keys()) {
      if (attribute === key.attribute) {
        if (!attributeValue) {
          return key; // Return the existing key that matches the value
        }
        if (attributeValue === key.attributeValue) {
          return key;
        }
      }
    }
    return undefined; // Return undefined if no matching key is found */
  }

  public dispose() {
    for (const sub of this._subs) {
      sub.unsubscribe();
    }
  }
}
